import { useState, useEffect } from 'react'
import { Grid, Theme, Typography, Divider } from '@material-ui/core'
import DownloadTemplate from '../DownloadTemplate'
import { useUploadTubesFromFileMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import {
  ActiveLinkType,
  useProductFromQueryParam,
  useProductInput,
} from '../ProductsConfiguration'
import CircleLoader from '@src/components/CircleLoader'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { FileFormat, StartRow, TubeProduct } from '../enum'
import { makeStyles } from '@material-ui/core/styles'
import { getOligoListFromFileUpload } from './MultipleProductEntryUtil'
import { parseSpreadsheet } from '../ParseSpreadsheet'
import CopyEntryMTORedesign from '@src/routes/Configurators/components/CopyEntryMTORedesign/CopyEntryMTORedesign'
import useFormikHelper from '../useFormikHelper'
import router from 'next/router'
import CautionPopupMTORedesign from '@src/routes/Configurators/components/CautionPopupMTORedesign'
import { isNil } from 'lodash'
import useCopyPasteHelper from '../useCopyPasteHelper'
import PageError from './PageError'
import dynamic from 'next/dynamic'

//file upload util does not work with ssr. this is to ensure we only render on client side
const UploadTemplate = dynamic(
  () => import('@src/routes/Configurators/components/UploadTemplate'),
  { ssr: false }
)

const useStyles = makeStyles((theme: Theme) => {
  return {
    errorAlert: {
      marginTop: theme.spacing(6),
    },
    separator: {
      textAlign: 'center',
      paddingLeft: theme.spacing(7),
    },
    fieldLabel: {
      marginTop: theme.spacing(5),
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.common.black,
      marginBottom: theme.spacing(5),
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5),
    },
    divider: {
      margin: '10px auto',
      height: '30%',
    },
  }
})

const MultipleProductEntry = () => {
  const intl = useIntl()
  const formik = useFormikHelper()
  const classes = useStyles()

  const { copyPaste } = useCopyPasteHelper()
  const product = useProductFromQueryParam()
  const [uploadTubesFromFile, { loading: fileUploadLoading }] =
    useUploadTubesFromFileMutation()

  const [validationMessage, setValidationMessage] = useState<string[]>([])
  const [upload, setUpload] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [fileDataValue, setFileDataValue] = useState<any>('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    formik.setFieldValue('validUploadTemplate', false, false)
    formik.setFieldValue('copypasteLoading', false, false)
    formik.setFieldValue('validationMessage', [], false)
    formik.setFieldValue('copiedEntry', '', false)
    formik.setFieldValue('displayPageError', false, false)
    formik.setFieldValue('showCautionPopup', false, false)
  }, [])

  const productInput = useProductInput()

  const handleText = (e) => {
    setUpload(false)
    setValidationMessage([])
    formik.setFieldValue('displayPageError', false, false)
    formik.setFieldValue('validationMessage', [], false)
    formik.setFieldValue('applyToNextOligos', false, false)
    let validUploadTemplate
    if (e.target.value.length !== 0) {
      validUploadTemplate = true
    } else {
      validUploadTemplate = false
    }

    formik.setFieldValue('validUploadTemplate', validUploadTemplate, false)
    formik.setFieldValue('copiedEntry', e.target.value, false)
  }

  const handleFileUploadApiResponse = (key, value) => {
    setValidationMessage([])
    formik.setFieldValue('copiedEntry', '', false)
    formik.setFieldValue('displayPageError', true, false)
    let validUploadTemplate = false
    if (key && key !== '') {
      if (
        key === messages.CONF_SUCCESS.defaultMessage ||
        (key === messages.CONF_REVIEW_ERROR.defaultMessage && value)
      ) {
        validUploadTemplate = true
        const oligoList = getOligoListFromFileUpload(value)
        formik.setFieldValue('oligoList', oligoList)
        const { country, language, product } = router.query
        router.replace(
          {
            pathname: router.pathname,
            query: {
              country: country,
              language: language,
              product: product,
              activeLink: ActiveLinkType.ConfirmOrder,
            },
          },
          undefined,
          { shallow: true }
        )
      } else {
        setLoading(false)
        setValidationMessage([key])
        formik.setFieldValue('displayPageError', true, false)
      }
      formik.setFieldValue('validUploadTemplate', validUploadTemplate, false)
    } else {
      setLoading(false)
    }
  }

  const uploadFile = async (file?: any, isCopyPaste?: boolean) => {
    if (isNil(file)) {
      file = fileDataValue
    }
    if (!isCopyPaste) {
      formik.setFieldValue('copiedEntry', '', false)
    }
    setDialogOpen(false)
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      const inputStr = await parseSpreadsheet(file, product)

      if (inputStr === FileFormat.InvalidTemplate) {
        setValidationMessage([
          intl.formatMessage(messages.CONF_INVALID_TEMPLATE),
        ])
        formik.setFieldValue('displayPageError', true, false)
      }
      if (inputStr === FileFormat.MultipleLineError) {
        setValidationMessage([intl.formatMessage(messages.MULTIPLE_LINE_ERROR)])
        formik.setFieldValue('displayPageError', true, false)
      }
      if (inputStr === '') {
        setValidationMessage([intl.formatMessage(messages.CONF_EMPTY_FILE)])
        formik.setFieldValue('displayPageError', true, false)
      }
      const startRow = parseInt(StartRow[product])
      if (
        inputStr !== '' &&
        inputStr !== FileFormat.MultipleLineError &&
        inputStr !== FileFormat.InvalidTemplate
      ) {
        const response = await uploadTubesFromFile({
          fetchPolicy: 'no-cache',
          variables: {
            input: inputStr,
            productInput,
            isOverride: false,
            isDForm: false,
            startRow,
          },
        })
        const { data } = response

        if (data && data.uploadTubesFromFile) {
          if (!data.uploadTubesFromFile.serverError) {
            const { key, value } = data.uploadTubesFromFile
            handleFileUploadApiResponse(key, value)
          } else {
            window.scrollTo(0, 0)
            setLoading(false)
          }
        } else {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    } else {
      if (file !== undefined) {
        setValidationMessage([intl.formatMessage(messages.CONF_INVALID_FORMAT)])
        //formik rest is slow. without timeout, the value will be reset to default due to form rest called before in handleContinue
        setTimeout(() => formik.setFieldValue('displayPageError', true, false))
      }
      setLoading(false)
    }
  }
  const handleFileUpload = async (file: any) => {
    setLoading(true)
    setUpload(true)
    setValidationMessage([])
    formik.setFieldValue('displayPageError', false, false)
    formik.setFieldValue('applyToNextOligos', false, false)
    if (formik?.allValues?.oligoList?.[0]?.name === '') {
      uploadFile(file)
    } else {
      setFileDataValue(file)
      setDialogOpen(true)
    }
  }

  const handleContinue = () => {
    formik.removeItemFromOligoList(null, true, false, true)
    if (formik.allValues?.showCautionPopup === true) {
      setDialogOpen(false)
      return copyPaste({ userWantsToContinue: true })
    } else {
      return uploadFile(null, true)
    }
  }

  const handleClose = () => {
    setLoading(false)
    setDialogOpen(false)
    formik.setFieldValue('showCautionPopup', false, false)
  }

  const showErrorAlert =
    validationMessage.length > 0 || formik.allValues?.displayPageError

  const isInLoadingState =
    loading || formik.allValues.copypasteLoading || fileUploadLoading

  return (
    <Grid container spacing={6} style={{ marginTop: '11px' }}>
      {showErrorAlert && (
        <Grid item xs={12}>
          <PageError />
        </Grid>
      )}
      <Grid item xs={12}>
        <FormattedMessage
          id="CONF_OPTIONAL_INTR_MOD_TEMPLATE_standard"
          defaultMessage="Follow these steps below to quickly upload multiple entries using a pre-defined template."
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP1"
                  defaultMessage="Step 1"
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage
                id="DOWNLOAD_ORDERING_TEMPLATE"
                defaultMessage="Download the ordering template you need"
              />
              {':'}
            </Grid>
          </Grid>
          <Grid container item spacing={3}>
            {product === TubeProduct.Probe ? (
              <>
                {' '}
                <Grid item xs={12}>
                  <DownloadTemplate
                    isPcr={false}
                    productType="PROBE_duallabel"
                    label={intl.formatMessage({
                      id: 'MULTIPLE_ENTRY_TEMPLATE_DUAL_LABELLED',
                      defaultMessage: 'Multiple Entry Template: Dual-Labeled',
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DownloadTemplate
                    isPcr={false}
                    productType="PROBE_internalquench"
                    label={intl.formatMessage({
                      id: 'MULTIPLE_ENTRY_TEMPLATE_INTERNAL_QUENCHED',
                      defaultMessage:
                        'Multiple Entry Template: Internal-Quenched',
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DownloadTemplate
                    isPcr={false}
                    productType="PROBE_dualquench"
                    label={intl.formatMessage({
                      id: 'MULTIPLE_ENTRY_TEMPLATE_DUAL_QUENCHED',
                      defaultMessage: 'Multiple Entry Template: Dual-Quenched',
                    })}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <DownloadTemplate
                  label={intl.formatMessage({
                    id: 'MULTIPLE_PRODUCT_ENTRY_TEMPLATE',
                    defaultMessage: 'Multiple Product Entry Template',
                  })}
                  isPcr={false}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              {product === TubeProduct.Standard && (
                <DownloadTemplate
                  label={intl.formatMessage({
                    id: 'CONF_PCR_NON_MODIFIED_OLIGOS',
                    defaultMessage: 'PCR Non-Modified Oligos',
                  })}
                  isPcr
                />
              )}
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP2"
                  defaultMessage="Step 2"
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage
                id="CONF_MULTIPLE_ENTRY_STEP_2"
                defaultMessage="Enter your specifications in the provided spreadsheet template."
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP3"
                  defaultMessage="Step 3"
                />
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <FormattedMessage
                id="CONF_UPLOAD_YOUR_DATA"
                defaultMessage="Upload Your Data"
              />{' '}
              {':'}
            </Grid>
            <Grid item xs={6}>
              <FormattedMessage
                id="CONF_COPY_PASTE_HEADING"
                defaultMessage="Copy and paste your entries from the provided template using one sequence per line"
              />
              {':'}
            </Grid>

            <Grid container>
              <Grid item sm={3}>
                <UploadTemplate
                  isConfiguratorsRedesign="true"
                  accept={FileFormat.AcceptFormat}
                  handleUploadChange={([file]) => handleFileUpload(file)}
                />
                {upload && (
                  <div>
                    <ValidationErrors
                      errors={validationMessage}
                      configuratorsRedesign
                    />
                  </div>
                )}
              </Grid>
              <>
                <Grid item sm={2} className={classes.separator}>
                  <Divider orientation="vertical" className={classes.divider} />
                  <span className={classes.fieldLabel}>
                    <FormattedMessage {...messages.CONF_OR} />
                  </span>
                  <Divider orientation="vertical" className={classes.divider} />
                </Grid>
                <Grid item sm={6}>
                  <CopyEntryMTORedesign
                    handleChange={handleText}
                    text={formik.allValues.copiedEntry}
                  />
                  {!upload && (
                    <div>
                      <ValidationErrors
                        errors={formik.allValues.validationMessage}
                      />
                    </div>
                  )}
                </Grid>
              </>
            </Grid>
          </Grid>
        </Grid>
        {isInLoadingState && <CircleLoader />}
      </Grid>
      <CautionPopupMTORedesign
        dialogOpen={dialogOpen || formik.allValues?.showCautionPopup === true}
        handleClose={handleClose}
        handleContinue={handleContinue}
      />
    </Grid>
  )
}

export default MultipleProductEntry
